/**
 * Engineering & Maintenance EM-1 workspace.
 *
 * Supervisor experience — answers the six questions locked in AR-5:
 *   1. What is broken?
 *   2. What is critical?
 *   3. Which rooms are unavailable?
 *   4. Which technician is busiest?
 *   5. Which work orders are overdue?
 *   6. Which preventive maintenance is due today?
 *
 * The technician mobile view lives at /engineering/me.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Wrench, AlertTriangle, Users, RefreshCw, Search, Zap, DoorClosed, ClipboardList, CheckCircle2, CalendarClock, MapPin, Filter, Plus, Siren, User, Ban, Clock, Inbox, CalendarCheck, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField, errorRing } from "@/components/form/FormField";
import { useFormValidation } from "@/hooks/useFormValidation";
import { required } from "@/lib/validators";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const PRIORITY_TONE = {
  LOW: "neutral", NORMAL: "info", HIGH: "warning",
  URGENT: "error", EMERGENCY: "error",
};
const STATE_LABEL = {
  reported: "Reported", assigned: "Assigned", accepted: "Accepted",
  in_progress: "In Progress", paused: "Paused",
  pending_inspection: "Waiting Inspection", completed: "Completed",
  closed: "Closed", cancelled: "Cancelled",
};
const STATE_TONE = {
  reported: "warning", assigned: "info", accepted: "info",
  in_progress: "warning", paused: "warning",
  pending_inspection: "warning", completed: "success",
  closed: "success", cancelled: "neutral",
};

export default function Engineering() {
  const [hotelId, setHotelId] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [board, setBoard] = useState({});
  const [inspections, setInspections] = useState([]);
  const [pmSchedules, setPmSchedules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [tab, setTab] = useState("board");
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [woDrawerId, setWoDrawerId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [inspDrawerId, setInspDrawerId] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/hotels");
      setHotels(data || []);
      if (data && data.length) setHotelId(data[0].id || data[0]._id);
    })();
  }, []);

  const refresh = useCallback(async () => {
    if (!hotelId) return;
    try {
      const [d, w, b, i, p, c, pr] = await Promise.all([
        api.get(`/engineering/dashboard?hotel_id=${hotelId}`),
        api.get(`/engineering/work-orders?hotel_id=${hotelId}&limit=300`),
        api.get(`/engineering/board?hotel_id=${hotelId}`),
        api.get(`/engineering/inspections`),
        api.get(`/engineering/pm-schedules?hotel_id=${hotelId}`),
        api.get(`/engineering/categories?active=true`),
        api.get(`/engineering/priorities`),
      ]);
      setDashboard(d.data);
      setWorkOrders(w.data);
      setBoard(b.data);
      setInspections(i.data);
      setPmSchedules(p.data);
      setCategories(c.data);
      setPriorities(pr.data);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load"); }
  }, [hotelId]);

  useEffect(() => { refresh(); }, [refresh]);

  const filteredWOs = useMemo(() => {
    let out = workOrders;
    if (stateFilter !== "all") out = out.filter(w => w.state === stateFilter);
    if (priorityFilter !== "all") out = out.filter(w => w.priority_code === priorityFilter);
    if (q) {
      const s = q.toLowerCase();
      out = out.filter(w =>
        (w.code || "").toLowerCase().includes(s) ||
        (w.title || "").toLowerCase().includes(s) ||
        (w.room_number || "").toString().toLowerCase().includes(s) ||
        (w.category_code || "").toLowerCase().includes(s));
    }
    return out;
  }, [workOrders, stateFilter, priorityFilter, q]);

  const t = dashboard?.tiles || {};

  return (
    <div className="space-y-6" data-testid="engineering-page">
      <PageHeader title="Engineering & Maintenance"
        subtitle="Restore rooms and facilities back to service. Operations-first workflow.">
        <div className="flex gap-2 items-center">
          <select value={hotelId || ""} onChange={(e) => setHotelId(e.target.value)}
                  className="rounded-lg border border-hocp-border px-3 py-2 text-sm"
                  data-testid="em-hotel-select">
            {hotels.map(h => (
              <option key={h.id || h._id} value={h.id || h._id}>{h.name}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="em-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="em-create-wo">
            <Plus className="w-3.5 h-3.5 mr-1"/> New Work Order
          </Button>
        </div>
      </PageHeader>

      {/* KPI tiles — answer the six operational questions */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="em-tiles">
        <Tile label="Open" icon={Wrench} value={t.open_work_orders} testid="tile-open"/>
        <Tile label="Critical" icon={Siren} value={t.critical} testid="tile-critical" tone="error"/>
        <Tile label="Guest Impact" icon={User} value={t.guest_impacting_open} testid="tile-guest-impact" tone="error"/>
        <Tile label="Rooms OOS" icon={Ban} value={t.rooms_out_of_service} testid="tile-oos"/>
        <Tile label="Rooms OOO" icon={AlertTriangle} value={t.rooms_out_of_order} testid="tile-ooo"/>
        <Tile label="Overdue" icon={Clock} value={t.sla_overdue} testid="tile-overdue" tone="error"/>
        <Tile label="Unassigned" icon={Inbox} value={t.unassigned} testid="tile-unassigned"/>
        <Tile label="PM Due Today" icon={CalendarCheck} value={t.pm_due_today} testid="tile-pm-due"/>
        <Tile label="Failed Today" icon={XCircle} value={t.failed_inspections_today} testid="tile-failed" tone="error"/>
        <Tile label="Technicians" icon={Users}
              value={`${t.technicians_available ?? 0}/${t.technicians_total ?? 0}`}
              testid="tile-technicians"/>
      </div>

      {/* Rooms unavailable — Q3 */}
      {(dashboard?.rooms_unavailable || []).length > 0 && (
        <Card padded testid="rooms-unavailable">
          <div className="flex items-center gap-2 mb-2">
            <DoorClosed className="w-4 h-4 text-hocp-error"/>
            <h3 className="font-display font-semibold text-[15px]">Rooms Unavailable</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {dashboard.rooms_unavailable.map(r => (
              <button key={r.work_order_id} onClick={() => setWoDrawerId(r.work_order_id)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-hocp-error/30 bg-hocp-error/5 hover:bg-hocp-error/10"
                data-testid={`room-unavailable-${r.room_id}`}>
                <b className="text-[13px]">Room {r.room_number || "—"}</b>
                <Badge tone="error" size="sm">{r.type === "out_of_service" ? "OOS" : "OOO"}</Badge>
                <span className="text-[11px] text-hocp-textTertiary">{r.code}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="inline-flex bg-hocp-alice/50 rounded-lg p-0.5 border border-hocp-border">
        {[
          { id: "board", label: "Work Order Board", icon: ClipboardList },
          { id: "technicians", label: "Technicians", icon: Users },
          { id: "inspections", label: "Inspections", icon: CheckCircle2 },
          { id: "pm", label: "PM Schedules", icon: CalendarClock },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`em-tab-${id}`}
                  className={`px-3 py-1.5 rounded-md text-[13px] font-medium flex items-center gap-1.5
                    ${tab === id ? "bg-white shadow-sm text-hocp-textPrimary" : "text-hocp-textSecondary"}`}>
            <Icon className="w-3.5 h-3.5"/> {label}
          </button>
        ))}
      </div>

      {tab === "board" && (
        <>
          <div className="flex gap-2 items-center flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-hocp-textTertiary"/>
              <Input placeholder="Search code, title, room, category..." aria-label="Search work orders"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     className="pl-9" data-testid="wo-search"/>
            </div>
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
                    className="rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="wo-state-filter">
              <option value="all">All states</option>
              {Object.entries(STATE_LABEL).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
            </select>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
                    className="rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="wo-priority-filter">
              <option value="all">All priorities</option>
              {priorities.map(p => (<option key={p.code} value={p.code}>{p.label}</option>))}
            </select>
          </div>
          <WOTable wos={filteredWOs} onOpen={setWoDrawerId}/>
        </>
      )}

      {tab === "technicians" &&
        <TechniciansPanel technicians={dashboard?.by_technician || []}/>}
      {tab === "inspections" &&
        <InspectionsList inspections={inspections} onOpen={setInspDrawerId}/>}
      {tab === "pm" &&
        <PMSchedulesPanel schedules={pmSchedules} categories={categories} priorities={priorities}
                          hotelId={hotelId} onChanged={refresh}/>}

      <WODrawer woId={woDrawerId} onClose={() => setWoDrawerId(null)}
                onChanged={refresh}/>
      <CreateWODialog open={createOpen} onClose={() => setCreateOpen(false)}
                        hotelId={hotelId} categories={categories} priorities={priorities}
                        onCreated={refresh}/>
      <InspectionDrawer inspectionId={inspDrawerId}
                          onClose={() => setInspDrawerId(null)}
                          onChanged={refresh}/>
    </div>
  );
}

function Tile({ label, value, tone, testid, icon: Icon, dot }) {
  const toneCls = tone === "error" ? "text-hocp-error" : "text-hocp-textPrimary";
  return (
    <div className="hocp-card p-4 flex flex-col" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary flex items-center gap-1.5">
        {dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dot }} aria-hidden />}
        {Icon && <Icon size={14} strokeWidth={1.5} className="shrink-0" />}
        {label}
      </div>
      <div className={`font-display font-bold text-3xl mt-1 tabular-nums ${toneCls}`}>{value ?? "—"}</div>
    </div>
  );
}

function WOTable({ wos, onOpen }) {
  if (!wos.length) return <p className="text-hocp-textTertiary italic text-sm px-1" data-testid="wo-empty">No work orders match.</p>;
  return (
    <div className="hocp-card overflow-x-auto" data-testid="wo-table">
      <table className="w-full text-[13px]">
        <thead className="bg-hocp-alice/40">
          <tr>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Code</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Title</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Category</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Location</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Priority</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">State</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Guest</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">SLA</th>
          </tr>
        </thead>
        <tbody>
          {wos.map(w => (
            <tr key={w.id} onClick={() => onOpen(w.id)}
                className={`border-t border-hocp-border hover:bg-hocp-alice/30 cursor-pointer
                              ${w.guest_impact ? "bg-hocp-error/5" : ""}`}
                data-testid={`wo-row-${w.id}`}>
              <td className="px-3 py-2 font-mono text-[11.5px]">{w.code}</td>
              <td className="px-3 py-2">{w.title}</td>
              <td className="px-3 py-2 text-[11.5px]">{w.category_code}</td>
              <td className="px-3 py-2 text-hocp-textSecondary">
                {w.location_type === "room"
                  ? <><MapPin className="w-3 h-3 inline"/> {w.room_number || "—"}</>
                  : (w.location_note || w.location_type)}
              </td>
              <td className="px-3 py-2"><Badge tone={PRIORITY_TONE[w.priority_code] || "neutral"} size="sm">{w.priority_code}</Badge></td>
              <td className="px-3 py-2"><Badge tone={STATE_TONE[w.state]} size="sm">{STATE_LABEL[w.state]}</Badge></td>
              <td className="px-3 py-2">
                {w.guest_impact && <Badge tone="error" size="sm" data-testid={`wo-guest-badge-${w.id}`}>GUEST</Badge>}
              </td>
              <td className="px-3 py-2 tabular-nums text-[11.5px]">
                <SlaChip sla={w.sla_target_at} state={w.state}/>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SlaChip({ sla, state }) {
  if (!sla || ["closed", "cancelled", "completed"].includes(state))
    return <span className="text-hocp-textTertiary">—</span>;
  const target = new Date(sla).getTime();
  const now = Date.now();
  const diffMin = Math.round((target - now) / 60000);
  const overdue = diffMin < 0;
  const label = overdue ? `${Math.abs(diffMin)}m late` : `${diffMin}m left`;
  return <span className={overdue ? "text-hocp-error font-medium" : "text-hocp-textSecondary"}>{label}</span>;
}

function TechniciansPanel({ technicians }) {
  if (!technicians.length) return <p className="text-hocp-textTertiary italic text-sm px-1">No technicians found.</p>;
  return (
    <div className="hocp-card overflow-x-auto" data-testid="technicians-panel">
      <table className="w-full text-[13px]">
        <thead className="bg-hocp-alice/40">
          <tr>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Technician</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Status</th>
            <th className="px-3 py-2 text-right text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Open WOs</th>
            <th className="px-3 py-2 text-right text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">SLA at Risk</th>
            <th className="px-3 py-2 text-right text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Done Today</th>
          </tr>
        </thead>
        <tbody>
          {technicians.map(t => (
            <tr key={t.technician_id} className="border-t border-hocp-border"
                data-testid={`tech-row-${t.technician_id}`}>
              <td className="px-3 py-2">{t.name}</td>
              <td className="px-3 py-2"><Badge tone={t.availability_status === "available" ? "success" : "neutral"} size="sm">{t.availability_status}</Badge></td>
              <td className="px-3 py-2 tabular-nums text-right">{t.work_orders_open}</td>
              <td className="px-3 py-2 tabular-nums text-right text-hocp-error">{t.sla_at_risk}</td>
              <td className="px-3 py-2 tabular-nums text-right">{t.work_orders_done_today}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InspectionsList({ inspections, onOpen }) {
  if (!inspections.length) return <p className="text-hocp-textTertiary italic text-sm px-1">No inspections yet.</p>;
  return (
    <div className="hocp-card overflow-x-auto" data-testid="inspections-list">
      <table className="w-full text-[13px]">
        <thead className="bg-hocp-alice/40">
          <tr>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">WO</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Room</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Outcome</th>
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-hocp-textTertiary">Completed</th>
          </tr>
        </thead>
        <tbody>
          {inspections.map(i => (
            <tr key={i.id} onClick={() => onOpen(i.id)}
                className="border-t border-hocp-border hover:bg-hocp-alice/30 cursor-pointer"
                data-testid={`insp-row-${i.id}`}>
              <td className="px-3 py-2 font-mono text-[11.5px]">{i.work_order_id?.slice(-6)}</td>
              <td className="px-3 py-2">{i.room_id?.slice(-6) || "—"}</td>
              <td className="px-3 py-2"><Badge tone={i.outcome === "passed" ? "success" : i.outcome === "failed" ? "error" : "warning"} size="sm">{i.outcome}</Badge></td>
              <td className="px-3 py-2 text-[11.5px] text-hocp-textSecondary">{i.outcome_at || "pending"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PMSchedulesPanel({ schedules, categories, priorities, hotelId, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "", title: "", category_code: categories[0]?.code || "",
    priority_code: "LOW", interval_days: 30, scope_type: "all_rooms",
    scope_note: "", estimated_minutes: 60, require_inspection: false,
  });

  const submit = async () => {
    try {
      await api.post("/engineering/pm-schedules", { ...form, hotel_id: hotelId });
      toast.success("PM schedule created");
      setShowForm(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  const runNow = async () => {
    try {
      const { data } = await api.post("/engineering/jobs/pm-generator/run");
      toast.success(`PM Generator: ${data.generated} WOs created`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="pm-schedules-panel">
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={runNow} data-testid="pm-run-now">
          <Zap className="w-3.5 h-3.5 mr-1"/> Run Generator Now
        </Button>
        <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="pm-add-schedule">
          <Plus className="w-3.5 h-3.5 mr-1"/> New Schedule
        </Button>
      </div>

      {showForm && (
        <Card padded testid="pm-form">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Code</span>
              <Input placeholder="e.g. HVAC_MONTHLY" value={form.code}
                     onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="pm-code"/></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Title</span>
              <Input placeholder="e.g. AC Quarterly Servicing" value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pm-title"/></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Category</span>
              <select value={form.category_code}
                      onChange={(e) => setForm({ ...form, category_code: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="pm-category">
                {categories.map(c => (<option key={c.code} value={c.code}>{c.label}</option>))}
              </select></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Priority</span>
              <select value={form.priority_code}
                      onChange={(e) => setForm({ ...form, priority_code: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="pm-priority">
                {priorities.map(p => (<option key={p.code} value={p.code}>{p.label}</option>))}
              </select></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Interval (days)</span>
              <Input type="number" placeholder="e.g. 90" value={form.interval_days}
                     onChange={(e) => setForm({ ...form, interval_days: parseInt(e.target.value) })} data-testid="pm-interval"/></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Scope</span>
              <select value={form.scope_type}
                      onChange={(e) => setForm({ ...form, scope_type: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="pm-scope-type">
                <option value="all_rooms">All Rooms</option>
                <option value="specific_rooms">Specific Rooms</option>
                <option value="common_area">Common Area</option>
                <option value="back_of_house">Back of House</option>
              </select></label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={submit} data-testid="pm-submit">Create</Button>
          </div>
        </Card>
      )}

      {schedules.length === 0 && !showForm && (
        <p className="text-hocp-textTertiary italic text-sm px-1">
          No PM schedules yet. Add your first recurring maintenance schedule.
        </p>
      )}
      {schedules.map(s => (
        <div key={s.id} className="hocp-card p-3 flex items-center justify-between"
              data-testid={`pm-item-${s.id}`}>
          <div>
            <div className="font-mono font-semibold text-[13px]">{s.code}</div>
            <div className="text-hocp-textSecondary text-[12.5px]">{s.title}</div>
            <div className="text-hocp-textTertiary text-[11px] mt-0.5">
              {s.category_code} · every {s.interval_days} days · {s.scope_type}
              {s.next_due_at && <> · next: {s.next_due_at.slice(0, 10)}</>}
            </div>
          </div>
          <Badge tone={s.active ? "success" : "neutral"} size="sm">{s.active ? "active" : "inactive"}</Badge>
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Create WO dialog
// ═════════════════════════════════════════════════════════════════════
function CreateWODialog({ open, onClose, hotelId, categories, priorities, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", category_code: "",
    priority_code: "", severity: "minor", source: "engineering",
    location_type: "room", room_id: "", location_note: "",
    guest_impact: null,
  });
  const [rooms, setRooms] = useState([]);
  const v = useFormValidation({
    title: [required("Title is required")],
    room_id: [(val) => (form.location_type === "room" && !val ? "Please select a room" : "")],
  });

  useEffect(() => {
    if (open && hotelId) {
      api.get(`/inventory/rooms?hotel_id=${hotelId}&limit=500`)
         .then(r => setRooms(r.data || []))
         .catch(() => setRooms([]));
      setForm(f => ({ ...f, category_code: categories[0]?.code || "" }));
      v.reset();
    }
  }, [open, hotelId, categories]);  // eslint-disable-line

  const submit = async () => {
    if (!v.validateAll(form)) { toast.error("Please fix the highlighted fields"); return; }
    try {
      const body = { ...form, hotel_id: hotelId };
      if (form.location_type !== "room") body.room_id = null;
      if (!body.priority_code) delete body.priority_code;
      if (body.guest_impact === null) delete body.guest_impact;
      const { data } = await api.post("/engineering/work-orders", body);
      toast.success(`Created ${data.code}`);
      onCreated();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="create-wo-dialog">
        <h2 className="font-display font-bold text-[19px] mb-3">New Work Order</h2>
        <div className="space-y-2">
          <FormField label="Title" htmlFor="wo-title" required error={v.fieldError("title")}>
            <Input id="wo-title" placeholder="e.g. AC not cooling" value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })} onBlur={() => v.handleBlur("title", form.title)}
                   className={errorRing(v.fieldError("title"))} data-testid="wo-title"/></FormField>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Description <span className="text-slate-400 font-normal">(optional)</span></span>
            <textarea placeholder="Add any extra context" value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm min-h-[70px]"
                      data-testid="wo-description"/></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Category</span>
              <select value={form.category_code}
                      onChange={(e) => setForm({ ...form, category_code: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="wo-category">
                {categories.map(c => (<option key={c.code} value={c.code}>{c.label}</option>))}
              </select></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Priority</span>
              <select value={form.priority_code}
                      onChange={(e) => setForm({ ...form, priority_code: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="wo-priority">
                <option value="">Auto (from rules)</option>
                {priorities.map(p => (<option key={p.code} value={p.code}>{p.label}</option>))}
              </select></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Severity</span>
              <select value={form.severity}
                      onChange={(e) => setForm({ ...form, severity: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="wo-severity">
                <option value="minor">Minor</option>
                <option value="moderate">Moderate</option>
                <option value="major">Major</option>
              </select></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Source</span>
              <select value={form.source}
                      onChange={(e) => setForm({ ...form, source: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="wo-source">
                <option value="engineering">Engineering</option>
                <option value="front_office">Front Office</option>
                <option value="housekeeping">Housekeeping</option>
                <option value="guest_complaint">Guest Complaint</option>
                <option value="restaurant">Restaurant</option>
                <option value="pool">Pool</option>
                <option value="preventive">Preventive</option>
              </select></label>
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Location type</span>
              <select value={form.location_type}
                      onChange={(e) => setForm({ ...form, location_type: e.target.value })}
                      className="w-full rounded-lg border border-hocp-border px-3 py-2 text-sm" data-testid="wo-location-type">
                <option value="room">Room</option>
                <option value="common_area">Common Area</option>
                <option value="back_of_house">Back of House</option>
              </select></label>
            {form.location_type === "room" && (
              <FormField label="Room" htmlFor="wo-room" required error={v.fieldError("room_id")}>
                <select id="wo-room" value={form.room_id}
                        onChange={(e) => { setForm({ ...form, room_id: e.target.value }); v.handleBlur("room_id", e.target.value); }}
                        className={`w-full rounded-lg border border-hocp-border px-3 py-2 text-sm ${errorRing(v.fieldError("room_id"))}`} data-testid="wo-room">
                  <option value="">Select room...</option>
                  {rooms.map(r => (
                    <option key={r.id || r._id} value={r.id || r._id}>{r.room_number}</option>
                  ))}
                </select></FormField>
            )}
          </div>
          {form.location_type !== "room" && (
            <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Location note</span>
              <Input placeholder="e.g. Lobby ceiling" value={form.location_note}
                     onChange={(e) => setForm({ ...form, location_note: e.target.value })} data-testid="wo-location-note"/></label>
          )}
          <label className="flex items-center gap-2 text-[13px] text-hocp-textSecondary">
            <input type="checkbox" checked={form.guest_impact === true}
                    onChange={(e) => setForm({ ...form, guest_impact: e.target.checked })}
                    data-testid="wo-guest-impact"/>
            Affects a guest (Guest Impact)
          </label>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} data-testid="wo-create-submit">Create</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ═════════════════════════════════════════════════════════════════════
// WO Drawer — Full lifecycle controls
// ═════════════════════════════════════════════════════════════════════
function WODrawer({ woId, onClose, onChanged }) {
  const [wo, setWo] = useState(null);
  const [history, setHistory] = useState([]);
  const [recs, setRecs] = useState(null);

  const load = useCallback(async () => {
    if (!woId) { setWo(null); setHistory([]); setRecs(null); return; }
    try {
      const [w, h] = await Promise.all([
        api.get(`/engineering/work-orders/${woId}`),
        api.get(`/engineering/work-orders/${woId}/history`),
      ]);
      setWo(w.data);
      setHistory(h.data);
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  }, [woId]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    try {
      await api.post(`/engineering/work-orders/${woId}${path}`, body || {});
      toast.success(`Done: ${path.slice(1)}`);
      load(); onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    }
  };

  const recommend = async () => {
    try {
      const { data } = await api.post(`/engineering/work-orders/${woId}/recommendations`);
      setRecs(data);
    } catch (e) { toast.error("Failed"); }
  };

  if (!woId) return null;
  return (
    <Sheet open={!!woId} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="wo-drawer">
        {wo && (<>
          <div className="flex items-center gap-2 mb-2">
            <h2 className="font-display font-bold text-[19px]">{wo.title}</h2>
            {wo.guest_impact && <Badge tone="error" size="sm">GUEST IMPACT</Badge>}
          </div>
          <div className="flex items-center gap-2 mb-4 text-[12px] text-hocp-textSecondary">
            <span className="font-mono">{wo.code}</span> ·
            <Badge tone={PRIORITY_TONE[wo.priority_code]} size="sm">{wo.priority_code}</Badge>
            <Badge tone={STATE_TONE[wo.state]} size="sm">{STATE_LABEL[wo.state]}</Badge>
            <span>{wo.category_code}</span>
          </div>
          {wo.description && <p className="text-[13px] mb-3 text-hocp-textSecondary">{wo.description}</p>}
          <div className="grid grid-cols-2 gap-2 text-[12px] mb-4">
            <Info label="Room" value={wo.room_number || wo.location_note || "—"}/>
            <Info label="Severity" value={wo.severity}/>
            <Info label="Source" value={wo.source}/>
            <Info label="Room Impact" value={wo.room_availability_impact}/>
            <Info label="Assigned to" value={wo.assigned_to ? wo.assigned_to.slice(-6) : "—"}/>
            <Info label="SLA target" value={wo.sla_target_at ? new Date(wo.sla_target_at).toLocaleString() : "—"}/>
            <Info label="Reported by" value={wo.reported_by?.slice(-6) || "—"}/>
            <Info label="Requires inspection" value={wo.require_inspection ? "Yes" : "No"}/>
          </div>

          {/* Action row */}
          <div className="flex flex-wrap gap-2 mb-4">
            {wo.state === "reported" && (
              <Button size="sm" onClick={recommend} data-testid="wo-recommend"><Users className="w-3.5 h-3.5 mr-1"/> Recommend</Button>
            )}
            {["reported","assigned","accepted","paused","in_progress"].includes(wo.state) && (
              <AssignInline onAssign={(tid) => act("/assign", { technician_id: tid })}/>
            )}
            {wo.state === "assigned" && (
              <Button size="sm" onClick={() => act("/accept")} data-testid="wo-accept">Accept</Button>
            )}
            {["assigned","accepted","paused"].includes(wo.state) && (
              <Button size="sm" onClick={() => act("/start")} data-testid="wo-start">Start</Button>
            )}
            {wo.state === "in_progress" && (
              <>
                <Button size="sm" variant="outline"
                        onClick={() => { const r = prompt("Reason?"); if (r) act("/pause", { reason: r }); }}
                        data-testid="wo-pause">Pause</Button>
                <Button size="sm" onClick={() => { const n = prompt("Resolution note?"); act("/complete", { resolution_note: n }); }}
                        data-testid="wo-complete">Complete</Button>
              </>
            )}
            {wo.state === "paused" && (
              <Button size="sm" onClick={() => act("/resume")} data-testid="wo-resume">Resume</Button>
            )}
            {wo.state === "completed" && (
              <Button size="sm" onClick={() => act("/close")} data-testid="wo-close">Close</Button>
            )}
            {["reported","assigned","accepted"].includes(wo.state) && (
              <Button size="sm" variant="outline"
                      onClick={() => { const r = prompt("Cancellation reason?"); if (r) act("/cancel", { reason: r }); }}
                      data-testid="wo-cancel">Cancel</Button>
            )}
            {wo.room_id && !["closed","cancelled"].includes(wo.state) && (
              <>
                <Button size="sm" variant="outline"
                        onClick={() => { const r = prompt("OOS reason (severity=major+guest_impact required)?"); if (r) act("/take-oos", { reason: r }); }}
                        data-testid="wo-take-oos">Take OOS</Button>
                <Button size="sm" variant="outline"
                        onClick={() => { const r = prompt("OOO reason?"); if (r) act("/take-ooo", { reason: r }); }}
                        data-testid="wo-take-ooo">Take OOO</Button>
              </>
            )}
            {wo.state === "closed" && (wo.room_taken_oos || wo.room_taken_ooo) && (
              <Button size="sm" onClick={() => act("/reinstate")} data-testid="wo-reinstate">Reinstate Room</Button>
            )}
          </div>

          {recs && (
            <div className="mb-4">
              <h3 className="font-semibold text-[13px] mb-2">Recommended Technicians</h3>
              {(recs.candidates || []).length === 0 && (
                <p className="text-[12px] italic text-hocp-textTertiary">
                  {recs.note || "No candidates."}
                </p>
              )}
              {(recs.candidates || []).slice(0, 5).map(c => (
                <div key={c.technician_id} className="flex items-center justify-between p-2 border border-hocp-border rounded-lg mb-1"
                        data-testid={`rec-tech-${c.technician_id}`}>
                  <div>
                    <div className="text-[12.5px] font-medium">{c.name}</div>
                    <div className="text-[11px] text-hocp-textTertiary">{c.reasons.join(" · ")}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => act("/assign", { technician_id: c.technician_id })}>
                    Assign — {c.score}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <h3 className="font-semibold text-[13px] mb-2">History</h3>
          <div className="space-y-1 text-[11.5px]">
            {history.map(h => (
              <div key={h.id} className="text-hocp-textSecondary">
                <span className="text-hocp-textTertiary">{h.at?.slice(0, 19)}</span>
                {" — "}
                <b>{h.event}</b>: {h.from_state} → {h.to_state}
                {h.reason && <> ({h.reason})</>}
              </div>
            ))}
          </div>
        </>)}
      </SheetContent>
    </Sheet>
  );
}

function AssignInline({ onAssign }) {
  const [uid, setUid] = useState("");
  return (
    <div className="flex gap-1">
      <Input value={uid} onChange={(e) => setUid(e.target.value)}
             placeholder="Technician ID" className="text-[11.5px] w-40"
             data-testid="wo-assign-input"/>
      <Button size="sm" variant="outline" onClick={() => uid && onAssign(uid)}
              data-testid="wo-assign-btn">Assign</Button>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-hocp-textTertiary">{label}</div>
      <div className="text-[12.5px] text-hocp-textPrimary">{value ?? "—"}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Inspection drawer
// ═════════════════════════════════════════════════════════════════════
function InspectionDrawer({ inspectionId, onClose, onChanged }) {
  const [insp, setInsp] = useState(null);
  useEffect(() => {
    if (!inspectionId) { setInsp(null); return; }
    api.get(`/engineering/inspections?work_order_id=`).then(r => {
      const found = (r.data || []).find(i => i.id === inspectionId);
      setInsp(found);
    });
  }, [inspectionId]);
  if (!inspectionId) return null;
  const submit = async (outcome) => {
    try {
      const failureReason = outcome === "failed" ? prompt("Failure reason?") : null;
      if (outcome === "failed" && !failureReason) return;
      await api.post(`/engineering/inspections/${inspectionId}/complete`, {
        outcome, results: [], failure_reason: failureReason,
      });
      toast.success(`Inspection ${outcome}`);
      onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  return (
    <Sheet open={!!inspectionId} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="inspection-drawer">
        <h2 className="font-display font-bold text-[19px] mb-3">Inspection</h2>
        {insp && (
          <div className="text-[13px] text-hocp-textSecondary mb-4">
            WO: {insp.work_order_id?.slice(-6)} · Outcome: <b>{insp.outcome}</b>
          </div>
        )}
        {insp?.outcome === "pending" && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => submit("failed")} data-testid="insp-fail">Fail</Button>
            <Button onClick={() => submit("passed")} data-testid="insp-pass">Pass</Button>
          </div>
        )}
        {insp?.outcome && insp.outcome !== "pending" && (
          <p className="text-[13px] italic">Already completed. {insp.failure_reason}</p>
        )}
      </SheetContent>
    </Sheet>
  );
}
